import Phaser from 'phaser';
import {
  adminActivity,
  adminApprove,
  adminListMatches,
  adminListUsers,
  adminMatchLogs,
  adminRankings,
  adminReject,
  adminUserActivity,
  adminUserMatches,
  type ActivityEvent,
  type AdminMatch,
  type AdminUser,
  type RankingRow,
} from '../network/api.js';
import { clearSession, getUser } from '../network/session.js';
import { resetOverlay } from '../ui/overlay.js';
import { mountBackdrop } from '../ui/backdrop.js';
import { transitionTo } from '../ui/transition.js';

type Tab = 'approvals' | 'rankings' | 'matches' | 'activity';

export class AdminScene extends Phaser.Scene {
  private overlay: HTMLDivElement | null = null;
  private tab: Tab = 'approvals';

  constructor() {
    super('Admin');
  }

  create(): void {
    resetOverlay();
    // Phaser reuses scene instances — reset transient state each visit.
    this.tab = 'approvals';

    mountBackdrop(this, { color: 0x1a0508, depth: -10 });

    this.mountUI();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unmountUI());
  }

  private goTo(key: string, data?: object): void {
    this.unmountUI();
    transitionTo(this, key, data);
  }

  private mountUI(): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const user = getUser();
    const wrap = document.createElement('div');
    wrap.style.cssText = `
      position: absolute; inset: 0;
      display: grid; grid-template-rows: 64px 48px 1fr;
      pointer-events: auto; box-sizing: border-box;
    `;
    wrap.innerHTML = `
      <header style="display:flex; align-items:center; padding:0 24px; background: linear-gradient(180deg, rgba(245,200,105,0.10) 0%, rgba(0,0,0,0) 100%); border-bottom:1px solid rgba(245,200,105,0.25); gap:14px;">
        <button id="dm-back" class="lux-btn-ghost" style="padding:6px 14px;">← 로비</button>
        <div style="font-size:18px; font-weight:800; color:#f5c869; letter-spacing:0.5px;">관리자 콘솔</div>
        <div style="flex:1;"></div>
        <div style="font-size:12px; color:rgba(244,236,214,0.7);">${escapeHtml(user?.email ?? user?.username ?? '')}</div>
        <button id="dm-logout" class="lux-btn-ghost" style="padding:6px 14px;">로그아웃</button>
      </header>
      <nav style="display:flex; align-items:center; gap:8px; padding:0 24px; border-bottom:1px solid rgba(245,200,105,0.15);">
        <button data-tab="approvals" class="dm-tab lux-btn-ghost" style="padding:6px 16px;">승인 관리</button>
        <button data-tab="rankings" class="dm-tab lux-btn-ghost" style="padding:6px 16px;">사용자·랭킹</button>
        <button data-tab="matches" class="dm-tab lux-btn-ghost" style="padding:6px 16px;">매치 기록</button>
        <button data-tab="activity" class="dm-tab lux-btn-ghost" style="padding:6px 16px;">접속 로그</button>
      </nav>
      <div id="dm-admin-body" class="lux-scroll" style="overflow:auto; padding:18px 24px; min-height:0;"></div>
    `;
    root.appendChild(wrap);
    this.overlay = wrap;

    wrap
      .querySelector<HTMLButtonElement>('#dm-back')!
      .addEventListener('click', () => this.goTo('Lobby'));
    wrap
      .querySelector<HTMLButtonElement>('#dm-logout')!
      .addEventListener('click', () => {
        clearSession();
        this.goTo('Login');
      });
    wrap.querySelectorAll<HTMLButtonElement>('.dm-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.tab = btn.dataset.tab as Tab;
        this.renderTabs();
        void this.renderBody();
      });
    });

    this.renderTabs();
    void this.renderBody();
  }

  private renderTabs(): void {
    this.overlay?.querySelectorAll<HTMLButtonElement>('.dm-tab').forEach((b) => {
      const active = b.dataset.tab === this.tab;
      b.classList.toggle('lux-btn-primary', active);
      b.classList.toggle('lux-btn-ghost', !active);
    });
  }

  private body(): HTMLDivElement | null {
    return this.overlay?.querySelector<HTMLDivElement>('#dm-admin-body') ?? null;
  }

  private async renderBody(): Promise<void> {
    const body = this.body();
    if (!body) return;
    body.innerHTML = `<div style="color:rgba(244,236,214,0.5); padding:40px; text-align:center;">불러오는 중…</div>`;
    try {
      if (this.tab === 'approvals') await this.renderApprovals(body);
      else if (this.tab === 'rankings') await this.renderRankings(body);
      else if (this.tab === 'matches') await this.renderMatches(body);
      else await this.renderActivity(body);
    } catch (err) {
      body.innerHTML = `<div style="color:#ffb878; padding:40px; text-align:center;">불러오기 실패: ${escapeHtml(
        (err as Error).message,
      )}</div>`;
    }
  }

  // --- 승인 관리 -----------------------------------------------------------

  private async renderApprovals(body: HTMLDivElement): Promise<void> {
    const { users } = await adminListUsers('all');
    if (this.body() !== body) return; // tab switched while loading
    const pending = users.filter((u) => u.status === 'pending');
    const others = users.filter((u) => u.status !== 'pending');
    body.innerHTML = `
      <h3 style="color:#f5c869; margin:0 0 10px;">승인 대기 <span style="color:#f4ecd6; font-weight:500;">${pending.length}명</span></h3>
      <div id="dm-pending">${pending.length ? '' : emptyBox('승인 대기 중인 사용자가 없습니다')}</div>
      <h3 style="color:#f5c869; margin:24px 0 10px;">전체 사용자 <span style="color:#f4ecd6; font-weight:500;">${others.length}명</span></h3>
      <div id="dm-others"></div>
    `;
    const pendingBox = body.querySelector<HTMLDivElement>('#dm-pending')!;
    pending.forEach((u) => pendingBox.appendChild(this.userRow(u, true)));
    const othersBox = body.querySelector<HTMLDivElement>('#dm-others')!;
    others.forEach((u) => othersBox.appendChild(this.userRow(u, false)));
  }

  private userRow(u: AdminUser, showActions: boolean): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'lux-list-row';
    row.style.cssText =
      'display:flex; align-items:center; gap:12px; margin-bottom:8px;';
    const statusChip =
      u.status === 'approved'
        ? '<span class="lux-chip lux-chip-play">승인</span>'
        : u.status === 'rejected'
          ? '<span class="lux-chip" style="background:rgba(167,36,36,0.25); color:#ff9a9a;">거절</span>'
          : '<span class="lux-chip lux-chip-wait">대기</span>';
    const roleChip =
      u.role === 'admin'
        ? '<span class="lux-chip" style="background:rgba(245,200,105,0.25); color:#f5c869;">ADMIN</span>'
        : '';
    row.innerHTML = `
      <div style="flex:1; min-width:0;">
        <div style="display:flex; align-items:center; gap:8px;">
          <span style="font-weight:700; color:#f4ecd6;">${escapeHtml(u.username)}</span>
          ${roleChip}${statusChip}
        </div>
        <div style="font-size:11px; color:rgba(244,236,214,0.55); margin-top:2px;">
          ${escapeHtml(u.email)} · 레이팅 ${u.rating} · 가입 ${fmtDate(u.createdAt)}
        </div>
        <div style="font-size:11px; color:rgba(244,236,214,0.45); margin-top:1px;">
          마지막 접속 ${u.lastLoginAt ? fmtDate(u.lastLoginAt) : '—'} · 로그인 ${u.loginCount}회
        </div>
      </div>
      ${
        showActions
          ? `<button class="dm-approve lux-btn-success" style="padding:6px 14px; font-size:12px;">승인</button>
             <button class="dm-reject lux-btn-danger" style="padding:6px 14px; font-size:12px;">거절</button>`
          : ''
      }
    `;
    row
      .querySelector<HTMLButtonElement>('.dm-approve')
      ?.addEventListener('click', async () => {
        await adminApprove(u.id).catch(() => {});
        void this.renderBody();
      });
    row
      .querySelector<HTMLButtonElement>('.dm-reject')
      ?.addEventListener('click', async () => {
        await adminReject(u.id).catch(() => {});
        void this.renderBody();
      });
    return row;
  }

  // --- 사용자·랭킹 ---------------------------------------------------------

  private async renderRankings(body: HTMLDivElement): Promise<void> {
    const { rankings } = await adminRankings();
    if (this.body() !== body) return;
    body.innerHTML = `
      <h3 style="color:#f5c869; margin:0 0 10px;">사용자 랭킹 <span style="color:#f4ecd6; font-weight:500;">${rankings.length}명</span></h3>
      <div style="display:flex; flex-direction:column; gap:6px;"></div>
    `;
    const list = body.querySelector<HTMLDivElement>('div')!;
    if (rankings.length === 0) {
      list.innerHTML = emptyBox('사용자가 없습니다');
      return;
    }
    rankings.forEach((r) => list.appendChild(this.rankingRow(r)));
  }

  private rankingRow(r: RankingRow): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'lux-list-row';
    row.style.cssText =
      'display:flex; align-items:center; gap:12px; cursor:pointer;';
    const winRate = r.games > 0 ? Math.round((r.wins / r.games) * 100) : 0;
    row.innerHTML = `
      <div style="width:36px; text-align:center; font-weight:900; color:#f5c869; font-size:16px;">${r.rank}</div>
      <div style="flex:1; min-width:0;">
        <div style="font-weight:700; color:#f4ecd6;">${escapeHtml(r.username)}</div>
        <div style="font-size:11px; color:rgba(244,236,214,0.55);">${escapeHtml(r.email)}</div>
      </div>
      <div style="text-align:right; font-size:12px; color:rgba(244,236,214,0.8);">
        <div>레이팅 <b style="color:#f5c869;">${r.rating}</b></div>
        <div style="font-size:11px; color:rgba(244,236,214,0.55);">${r.games}판 · ${r.wins}승 (${winRate}%)</div>
      </div>
    `;
    row.addEventListener('click', () => void this.showUserHistory(r.id));
    return row;
  }

  private async showUserHistory(userId: string): Promise<void> {
    const [data, activity] = await Promise.all([
      adminUserMatches(userId).catch((err) => {
        alert(`이력 불러오기 실패: ${(err as Error).message}`);
        return null;
      }),
      adminUserActivity(userId).catch(() => ({ events: [] as ActivityEvent[] })),
    ]);
    if (!data) return;
    const rows = data.matches
      .map(
        (m) => `
        <tr style="border-top:1px solid rgba(245,200,105,0.12);">
          <td style="padding:6px 8px; color:rgba(244,236,214,0.8);">${fmtDate(m.startedAt)}</td>
          <td style="padding:6px 8px; text-align:center;">${
            m.finishPlacement === null ? '-' : `${m.finishPlacement + 1}등`
          }</td>
          <td style="padding:6px 8px; text-align:center;">${escapeHtml(m.finalTitle ?? '-')}</td>
          <td style="padding:6px 8px; text-align:right; color:${(m.ratingDelta ?? 0) >= 0 ? '#82dd9c' : '#ff9a9a'};">${
            m.ratingDelta === null
              ? '-'
              : `${m.ratingDelta >= 0 ? '+' : ''}${m.ratingDelta}`
          }</td>
        </tr>`,
      )
      .join('');
    this.openModal(
      `${escapeHtml(data.user.username)} 전적`,
      `
        <div style="display:flex; gap:18px; margin-bottom:12px; font-size:13px; color:rgba(244,236,214,0.85);">
          <div>레이팅 <b style="color:#f5c869;">${data.stats.rating}</b></div>
          <div>${data.stats.games}판</div>
          <div>${data.stats.wins}승</div>
          <div>평균 ${
            data.stats.avgPlacement === null
              ? '-'
              : (data.stats.avgPlacement + 1).toFixed(1) + '등'
          }</div>
        </div>
        ${
          data.matches.length === 0
            ? emptyBox('완료된 게임 기록이 없습니다')
            : `<table style="width:100%; border-collapse:collapse; font-size:12px;">
                 <thead><tr style="color:#f5c869; text-align:left;">
                   <th style="padding:6px 8px;">일시</th>
                   <th style="padding:6px 8px; text-align:center;">등수</th>
                   <th style="padding:6px 8px; text-align:center;">직위</th>
                   <th style="padding:6px 8px; text-align:right;">레이팅</th>
                 </tr></thead>
                 <tbody>${rows}</tbody>
               </table>`
        }
        <div style="font-size:11px; color:#f5c869; font-weight:700; margin:16px 0 8px;">접속·활동 이력 (최근 ${activity.events.length})</div>
        ${
          activity.events.length === 0
            ? emptyBox('활동 기록이 없습니다')
            : `<div style="max-height:200px; overflow:auto; font-size:12px; display:flex; flex-direction:column; gap:3px;">
                 ${activity.events.map((e) => activityLine(e)).join('')}
               </div>`
        }
      `,
    );
  }

  // --- 접속 로그 -----------------------------------------------------------

  private async renderActivity(body: HTMLDivElement): Promise<void> {
    const { events } = await adminActivity(200);
    if (this.body() !== body) return;
    body.innerHTML = `
      <h3 style="color:#f5c869; margin:0 0 10px;">접속 로그 <span style="color:#f4ecd6; font-weight:500;">최근 ${events.length}건</span></h3>
      <div style="font-size:11px; color:rgba(244,236,214,0.5); margin-bottom:8px;">로그인 · 로비/방 입퇴장 · IP/브라우저</div>
      <div style="display:flex; flex-direction:column; gap:3px; font-size:12px;"></div>
    `;
    const list = body.querySelector<HTMLDivElement>('div:last-child')!;
    if (events.length === 0) {
      list.innerHTML = emptyBox('접속 기록이 없습니다');
      return;
    }
    list.innerHTML = events.map((e) => activityLine(e, true)).join('');
  }

  // --- 매치 기록 -----------------------------------------------------------

  private async renderMatches(body: HTMLDivElement): Promise<void> {
    const { matches } = await adminListMatches(100);
    if (this.body() !== body) return;
    body.innerHTML = `
      <h3 style="color:#f5c869; margin:0 0 10px;">매치 기록 <span style="color:#f4ecd6; font-weight:500;">${matches.length}건</span></h3>
      <div style="display:flex; flex-direction:column; gap:8px;"></div>
    `;
    const list = body.querySelector<HTMLDivElement>('div')!;
    if (matches.length === 0) {
      list.innerHTML = emptyBox('매치 기록이 없습니다');
      return;
    }
    matches.forEach((m) => list.appendChild(this.matchRow(m)));
  }

  private matchRow(m: AdminMatch): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'lux-list-row';
    row.style.cssText = 'cursor:pointer;';
    const finished = m.endedAt
      ? '<span class="lux-chip lux-chip-play">종료</span>'
      : '<span class="lux-chip lux-chip-wait">진행/중단</span>';
    const ranked = [...m.players]
      .filter((p) => p.finishPlacement !== null)
      .sort((a, b) => (a.finishPlacement ?? 0) - (b.finishPlacement ?? 0));
    const players = (ranked.length ? ranked : m.players)
      .map((p) => {
        const place =
          p.finishPlacement === null ? '' : `${p.finishPlacement + 1}. `;
        const bot = p.isBot ? ' 🤖' : '';
        return `<span style="margin-right:10px;">${place}${escapeHtml(p.displayName)}${bot}</span>`;
      })
      .join('');
    row.innerHTML = `
      <div style="display:flex; align-items:center; gap:8px; margin-bottom:4px;">
        <span style="font-weight:700; color:#f4ecd6;">${fmtDate(m.startedAt)}</span>
        ${finished}
        <span style="font-size:11px; color:rgba(244,236,214,0.55);">${m.rounds}라운드 · ${m.revolutions}혁명 · ${escapeHtml(m.mode)}</span>
      </div>
      <div style="font-size:12px; color:rgba(244,236,214,0.7);">${players}</div>
    `;
    row.addEventListener('click', () => void this.showMatchLogs(m));
    return row;
  }

  private async showMatchLogs(m: AdminMatch): Promise<void> {
    const data = await adminMatchLogs(m.id).catch((err) => {
      alert(`로그 불러오기 실패: ${(err as Error).message}`);
      return null;
    });
    if (!data) return;
    const lines = data.logs.length
      ? data.logs
          .map(
            (l) =>
              `<div style="padding:2px 0; color:rgba(244,236,214,0.8);"><span style="color:rgba(245,200,105,0.5); display:inline-block; width:34px;">${l.seq}</span> ${escapeHtml(l.text)}</div>`,
          )
          .join('')
      : emptyBox('저장된 로그가 없습니다');
    this.openModal(
      `매치 로그 · ${fmtDate(m.startedAt)}`,
      `<div style="font-size:12px; max-height:60vh; overflow:auto;">${lines}</div>`,
    );
  }

  // --- modal helper --------------------------------------------------------

  private openModal(title: string, innerHtml: string): void {
    const root = document.getElementById('overlay');
    if (!root) return;
    const backdrop = document.createElement('div');
    backdrop.style.cssText = `
      position: fixed; inset: 0; background: rgba(10,4,6,0.75);
      backdrop-filter: blur(4px); display:flex; align-items:center;
      justify-content:center; pointer-events:auto; z-index:200;
    `;
    const modal = document.createElement('div');
    modal.className = 'lux-panel';
    modal.style.cssText =
      'width:620px; max-height:80vh; overflow:auto; pointer-events:auto; padding:22px;';
    modal.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center;">
        <h3 style="margin:0;">${title}</h3>
        <button id="dm-modal-close" class="lux-btn-ghost" style="padding:4px 10px; font-size:14px;">✕</button>
      </div>
      <div class="lux-divider"></div>
      <div style="margin-top:8px;">${innerHtml}</div>
    `;
    backdrop.appendChild(modal);
    root.appendChild(backdrop);
    const close = (): void => backdrop.remove();
    modal
      .querySelector<HTMLButtonElement>('#dm-modal-close')!
      .addEventListener('click', close);
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });
  }

  private unmountUI(): void {
    this.overlay?.remove();
    this.overlay = null;
  }
}

function emptyBox(text: string): string {
  return `<div style="padding:30px; text-align:center; color:rgba(244,236,214,0.4); font-size:13px;">${escapeHtml(text)}</div>`;
}

const EVENT_META: Record<string, { label: string; color: string }> = {
  login: { label: '로그인', color: '#82dd9c' },
  lobby_join: { label: '로비 입장', color: '#6db4e0' },
  lobby_leave: { label: '로비 퇴장', color: 'rgba(244,236,214,0.5)' },
  room_join: { label: '방 입장', color: '#f5c869' },
  room_leave: { label: '방 퇴장', color: 'rgba(244,236,214,0.5)' },
};

// One activity-log line. `withUser` prepends the username (global feed).
function activityLine(e: ActivityEvent, withUser = false): string {
  const meta = EVENT_META[e.type] ?? {
    label: e.type,
    color: 'rgba(244,236,214,0.7)',
  };
  const who =
    withUser && e.user
      ? `<span style="color:#f4ecd6; font-weight:700;">${escapeHtml(e.user.username)}</span> `
      : '';
  const room = e.roomId
    ? ` <span style="color:rgba(244,236,214,0.4);">#${escapeHtml(e.roomId)}</span>`
    : '';
  const ip = e.ip
    ? ` <span style="color:rgba(244,236,214,0.35);">${escapeHtml(e.ip)}</span>`
    : '';
  return `<div style="display:flex; gap:8px; align-items:baseline; padding:2px 0;">
    <span style="color:rgba(244,236,214,0.5); flex:0 0 116px;">${fmtDate(e.at)}</span>
    ${who}<span style="color:${meta.color}; font-weight:600;">${meta.label}</span>${room}${ip}
  </div>`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
